// src/pages/customers/CustomerAddresses/AddressEditForm.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { CustomerAddressesAPI } from '../../../api/v3/customers-api-v3'
import {
  fetchProvinces,
  fetchCities,
  fetchDistricts,
  fetchVillages,
  fetchPostalCodesByVillage,
} from '../../../api/v3/wilayah-api-v3'

const BASE = '/app/sales/bagspa'

// ====== Region data sources (free) ======
// const GEONESIA_BASE   = 'https://cdn.jsdelivr.net/gh/rezzvy/geonesia-api/data'
// const URL_PROVINCES   = `${GEONESIA_BASE}/main.json`
// const URL_CITIES      = (provId)  => `${GEONESIA_BASE}/cities/${provId}.json`
// const URL_DISTRICTS   = (cityId)  => `${GEONESIA_BASE}/districts/${cityId}.json`
// const URL_VILLAGES    = (distrId) => `${GEONESIA_BASE}/villages/${distrId}.json`

// Postal code suggestions (Biteship)
const BITESHIP_AREAS  = 'https://api.biteship.com/v1/maps/areas'
const BITESHIP_KEY =
  (typeof import.meta !== 'undefined' ? import.meta.env?.VITE_BITESHIP_API_KEY : undefined) ||
  (typeof process !== 'undefined' ? process.env?.REACT_APP_BITESHIP_API_KEY : undefined)

// Default address fields (no lat/lng)
const INIT = {
  street: '',
  subdistrict: '',
  district: '',
  city: '',
  province: '',
  postal_code: '',
  recipient_name: '',
  telephone: '',
  alias: '',
  is_customer_default: true,
  is_seller_default: false,
}

// ------------ helpers ------------
function unwrapItem(payload) {
  if (!payload) return null
  if (payload.item) return payload.item
  if (payload.data && !Array.isArray(payload.data)) return payload.data
  if (payload.id) return payload
  return null
}

function useSafeParams() {
  const p = useParams();

  // these are the explicit names we support
  const explicitCustomer = p.customerId ?? p.customer_id;
  const explicitAddress  = p.addressId  ?? p.address_id  ?? p.addrId ?? p.aid ?? p.address;

  let customerId = explicitCustomer;
  let addressId  = explicitAddress;

  // If the route is /customers/:id/addresses/:addressId/edit
  // useParams() might be { id: '9', addressId: '11' }
  if (!customerId && p.id && explicitAddress) customerId = p.id;

  // If the route is /customers/:customerId/addresses/:id/edit
  // useParams() might be { customerId: '9', id: '11' }
  if (!addressId && p.id && explicitCustomer) addressId = p.id;

  // Absolute last-resort fallbacks (rare/legacy)
  if (!customerId && !addressId && p.id) customerId = p.id;
  if (!addressId && p.id2) addressId = p.id2;

  return { customerId, addressId };
}

async function fetchJSON(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`)
  return res.json()
}

// const fetchProvinces  = () => fetchJSON(URL_PROVINCES) // -> [{id, province}]
// const fetchCities     = async (pid) => (pid ? (await fetchJSON(URL_CITIES(pid)))?.city ?? [] : [])
// const fetchDistricts  = (cid) => (cid ? fetchJSON(URL_DISTRICTS(cid)) : [])
// const fetchVillages   = (did) => (did ? fetchJSON(URL_VILLAGES(did)) : [])

async function fetchPostalCodes({ subdistrict, district, city, province }) {
  if (!BITESHIP_KEY) return []
  const input = [subdistrict || district, city, province].filter(Boolean).join(', ')
  if (!input) return []
  const url = `${BITESHIP_AREAS}?countries=ID&input=${encodeURIComponent(input)}&type=single`
  const res = await fetch(url, { headers: { Authorization: `Bearer ${BITESHIP_KEY}` } })
  if (!res.ok) return []
  const json = await res.json()
  return [...new Set((json?.areas || []).map(a => String(a.postal_code)).filter(Boolean))]
}

function findByName(list, nameKey, target) {
  if (!target) return null
  const needle = String(target).toLowerCase()
  return list.find(o => String(o[nameKey]).toLowerCase() === needle) || null
}

// Required validation
function validateAddress(f) {
  const errors = []
  const req = [
    ['street', 'Street'],
    ['province', 'Province'],
    ['city', 'City'],
    ['district', 'District'],
    ['subdistrict', 'Subdistrict'],
    ['postal_code', 'Postal Code'],
    ['recipient_name', 'Recipient Name'],
    ['telephone', 'Telephone'],
  ]

  req.forEach(([k, label]) => {
    if (!f?.[k]?.toString().trim()) errors.push(label)
  })

  return errors
}

// ============ Component ============
export default function AddressEditForm() {
  const history = useHistory()
  const { customerId, addressId } = useSafeParams()
  const isEdit = Boolean(addressId)
  const formRef = useRef(null)

  const [f, setF] = useState(INIT)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [error, setError] = useState('')

  // Lists & selections
  const [provinces, setProvinces] = useState([])      // [{id, province}]
  const [cities, setCities] = useState([])            // [{id, name}]
  const [districts, setDistricts] = useState([])      // [{id, name}]
  const [subdistricts, setSubdistricts] = useState([])// [{id, name}]
  const [postalCodes, setPostalCodes] = useState([])  // ['10220', ...]

  const [provinceId, setProvinceId] = useState('')
  const [cityId, setCityId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [subdistrictId, setSubdistrictId] = useState('')

  const title = useMemo(() => (isEdit ? 'Edit Address' : 'New Address'), [isEdit])

  // Load provinces on mount
  useEffect(() => {
    fetchProvinces().then(setProvinces).catch(() => setProvinces([]))
  }, [])

  // Load cities/districts/subdistricts by cascade
  useEffect(() => {
    if (!provinceId) { setCities([]); setCityId(''); return }
    fetchCities(provinceId).then(setCities).catch(() => setCities([]))
  }, [provinceId])

  useEffect(() => {
    if (!cityId) { setDistricts([]); setDistrictId(''); return }
    fetchDistricts(cityId).then(setDistricts).catch(() => setDistricts([]))
  }, [cityId])

  useEffect(() => {
    if (!districtId) { setSubdistricts([]); setSubdistrictId(''); return }
    fetchVillages(districtId).then(setSubdistricts).catch(() => setSubdistricts([]))
  }, [districtId])

  // Reflect selection -> text fields on f; reset postal on subdistrict change
  useEffect(() => {
    const provName = provinces.find(p => p.id === provinceId)?.province || ''
    const cityName = cities.find(c => c.id === cityId)?.name || ''
    const distName = districts.find(d => d.id === districtId)?.name || ''
    const subdName = subdistricts.find(v => v.id === subdistrictId)?.name || ''

    setF(prev => ({
      ...prev,
      province: provName,
      city: cityName,
      district: distName,
      subdistrict: subdName,
      postal_code: (prev.subdistrict !== subdName) ? '' : prev.postal_code,
    }))
  }, [provinceId, cityId, districtId, subdistrictId, provinces, cities, districts, subdistricts])

  // Suggest postal codes when region changes
  // useEffect(() => {
  //   const { province, city, district, subdistrict } = f
  //   if (!province || !city || !district) { setPostalCodes([]); return }
  //   fetchPostalCodes({ province, city, district, subdistrict })
  //     .then(setPostalCodes)
  //     .catch(() => setPostalCodes([]))
  // }, [f.province, f.city, f.district, f.subdistrict])

  useEffect(() => {
    if (!subdistrictId) {
      setPostalCodes([])
      return
    }

    fetchPostalCodesByVillage(subdistrictId)
      .then(setPostalCodes)
      .catch(() => setPostalCodes([]))
  }, [subdistrictId])

  // Load existing address in edit mode and preselect region ids
  useEffect(() => {
    let ignore = false
    ;(async () => {
      if (!isEdit || !customerId || !addressId) { setLoading(false); return }
      try {
        setLoading(true)
        const resp = await CustomerAddressesAPI.show(customerId, addressId)
        const item = unwrapItem(resp) || {}

        const next = {
          street:          item.street || item.street_address || '',
          subdistrict:     item.subdistrict || item.sub_district || '',
          district:        item.district || '',
          city:            item.city || '',
          province:        item.province || '',
          postal_code:     String(item.postal_code || ''),
          recipient_name:  item.recipient_name || item.recipientName || '',
          telephone:       item.telephone || item.phone || '',
          alias:           item.alias || '',
          is_customer_default: Boolean(item.is_customer_default ?? item.isCustomerDefault ?? true),
          is_seller_default:   Boolean(item.is_seller_default   ?? item.isSellerDefault   ?? false),
        }
        if (!ignore) setF(next)

        // wait for provinces loaded then map names -> ids
        const wait = (ms) => new Promise(r => setTimeout(r, ms))
        for (let i = 0; i < 10; i++) { if (provinces.length) break; await wait(120) }

        const p = findByName(provinces, 'province', next.province)
        if (p) setProvinceId(p.id)

        const cs = p ? await fetchCities(p.id) : []
        if (!ignore) setCities(cs)
        const cty = findByName(cs, 'name', next.city)
        if (cty) setCityId(cty.id)

        const ds = cty ? await fetchDistricts(cty.id) : []
        if (!ignore) setDistricts(ds)
        const d = findByName(ds, 'name', next.district)
        if (d) setDistrictId(d.id)

        const vs = d ? await fetchVillages(d.id) : []
        if (!ignore) setSubdistricts(vs)
        const v = findByName(vs, 'name', next.subdistrict)
        if (v) setSubdistrictId(v.id)
      } catch (e) {
        if (!ignore) setError(e.message || 'Failed to load address')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [customerId, addressId, isEdit, provinces.length]) // provinces.length so it runs once provinces are ready

  // controlled inputs
  const up = (k) => (e) => {
    const { type, checked, value } = e.target
    setF((s) => ({ ...s, [k]: type === 'checkbox' ? checked : value }))
  }

  async function onSubmit(e) {
    e.preventDefault()
    setError('')

    // Browser validity UI
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity()
      return
    }

    // Custom required validation
    const missing = validateAddress(f)
    if (missing.length) {
      const msg = `Please fill required fields:\n- ${missing.join('\n- ')}`
      setError(msg)
      alert(msg)
      return
    }

    try {
      if (!customerId) throw new Error('Missing customer id from route.')
      setSaving(true)

      const payload = {
        street:          f.street,
        subdistrict:     f.subdistrict,
        district:        f.district,
        city:            f.city,
        province:        f.province,
        postal_code:     f.postal_code,
        recipient_name:  f.recipient_name,
        telephone:       f.telephone,
        alias:           f.alias,
        is_customer_default: !!f.is_customer_default,
        is_seller_default:   !!f.is_seller_default,
      }

      if (isEdit) {
        await CustomerAddressesAPI.update(customerId, addressId, payload)
        history.push(`${BASE}/customers/${customerId}/addresses/${addressId}`)
      } else {
        const res  = await CustomerAddressesAPI.create(customerId, payload)
        const item = unwrapItem(res)
        history.push(`${BASE}/customers/${customerId}/addresses/${item?.id || ''}`)
      }
    } catch (e) {
      setError(e.message || 'Failed to save address')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div>Loading…</div>

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{title}</h2>
        <div>
          <Link className="btn btn-light" to={`${BASE}/customers/${customerId}`}>Back</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}

      <form onSubmit={onSubmit} ref={formRef} noValidate>
        <div className="form-group">
          <label>Street <span className="text-danger">*</span></label>
          <input className="form-control" value={f.street} onChange={up('street')} required />
        </div>

        {/* Cascading selects */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Province <span className="text-danger">*</span></label>
            <select className="form-control" value={provinceId}
              onChange={e => { setProvinceId(e.target.value); setCityId(''); setDistrictId(''); setSubdistrictId(''); }}>
              <option value="">Select Province…</option>
              {provinces.map(p => <option key={p.id} value={p.id}>{p.province}</option>)}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label>City/Regency <span className="text-danger">*</span></label>
            <select className="form-control" value={cityId}
              onChange={e => { setCityId(e.target.value); setDistrictId(''); setSubdistrictId(''); }}
              disabled={!provinceId}>
              <option value="">{provinceId ? 'Select City/Regency…' : 'Select Province first'}</option>
              {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <label>District (Kecamatan) <span className="text-danger">*</span></label>
            <select className="form-control" value={districtId}
              onChange={e => { setDistrictId(e.target.value); setSubdistrictId(''); }}
              disabled={!cityId}>
              <option value="">{cityId ? 'Select District…' : 'Select City first'}</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label>Subdistrict (Kelurahan/Desa) <span className="text-danger">*</span></label>
            <select className="form-control" value={subdistrictId}
              onChange={e => setSubdistrictId(e.target.value)}
              disabled={!districtId}>
              <option value="">{districtId ? 'Select Subdistrict…' : 'Select District first'}</option>
              {subdistricts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {/* Postal Code (auto via Biteship; manual fallback) */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Postal Code <span className="text-danger">*</span></label>
            {postalCodes.length > 0 ? (
              <select className="form-control" value={f.postal_code}
                onChange={up('postal_code')}>
                <option value="">Select Postal Code…</option>
                {postalCodes.map(pc => <option key={pc} value={pc}>{pc}</option>)}
              </select>
            ) : (
              <input className="form-control"
                placeholder={BITESHIP_KEY ? 'No suggestions — type manually' : 'Please type manually for postal code'}
                value={f.postal_code} onChange={up('postal_code')} />
            )}
          </div>

          <div className="form-group col-md-3">
            <label>Recipient Name <span className="text-danger">*</span></label>
            <input className="form-control" value={f.recipient_name} onChange={up('recipient_name')} />
          </div>
          <div className="form-group col-md-3">
            <label>Telephone <span className="text-danger">*</span></label>
            <input className="form-control" value={f.telephone} onChange={up('telephone')} />
          </div>
        </div>

        <div className="form-group">
          <label>Alias (e.g., Home, Office)</label>
          <input className="form-control" value={f.alias} onChange={up('alias')} />
        </div>

        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="is_customer_default"
            checked={!!f.is_customer_default} onChange={up('is_customer_default')} />
          <label className="form-check-label" htmlFor="is_customer_default">Use as default shipping address</label>
        </div>

        <div className="form-check mb-3">
          <input className="form-check-input" type="checkbox" id="is_seller_default"
            checked={!!f.is_seller_default} onChange={up('is_seller_default')} />
          <label className="form-check-label" htmlFor="is_seller_default">Use as default pickup address</label>
        </div>

        <button className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  )
}
